import { HorizontalLineElementSaveModel } from "client/Api/Hub/common.composer";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ensureNotNil,
	generateTrackingId,
	setTrackingId
} from "client/Misc/utils";
import {
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useRef
} from "react";
import { useBuilderEditor } from "../../useBuilderEditor";
import styles from "./HorizontalLine.module.less";
import { ElementLayout } from "../ElementLayout";
import { ElementGenericActionHandlers, ElementGenericHandle } from "../Element";
import { getClientId, hasClientId } from "client/Misc/clientId";
import { Guid } from "client_legacy/ndaw/guid";

export type HorizontalLineProps = {
	attachable: IValueInputAttachable<HorizontalLineElementSaveModel>;
	genericHandlers: ElementGenericActionHandlers;
	ref: Ref<ElementGenericHandle>;
};

export const HorizontalLine = (props: HorizontalLineProps): ReactElement => {
	const {
		attachable,
		genericHandlers,
		ref
	} = props;

	const rootRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const {
		onAddNewElementToColumn,
		onOpenEmbedInputDialog,
		onOpenVideoInputDialog,
		onSelectImage,
		onMoveUp,
		onMoveDown,
		onDuplicate,
		onDelete
	} = genericHandlers;

	const {
		activate
	} = useBuilderEditor();

	useImperativeHandle(
		ref,
		(): ElementGenericHandle => ({
			findAndFocus: (clientId: Guid): void => {
				if (hasClientId(attachable.value) && getClientId(attachable.value) === clientId) {
					ensureNotNil(rootRef.current)
						.focus();
				}
			},
			scrollIntoView: (): void => {
				ensureNotNil(rootRef.current)
					.scrollIntoView();
			}
		}),
		[attachable.value]
	);

	return (
		<ElementLayout
			hasError={!attachable.validation.isValid()}
			trackingId={attachable.reducer.path}
			ref={rootRef}
		>
			<ElementLayout.ActionsMenu trackingId="element-actions">
				<ElementLayout.AddElementMenuItem
					onAddNewElementToColumn={onAddNewElementToColumn}
					onOpenEmbedInputDialog={onOpenEmbedInputDialog}
					onOpenVideoInputDialog={onOpenVideoInputDialog}
					onSelectImage={onSelectImage}
					trackingId={generateTrackingId(attachable.reducer.path, "__add-element-menu")}
				/>
				{onMoveUp && (
					<ElementLayout.MoveUpMenuItem
						onClick={onMoveUp}
					/>
				)}
				{onMoveDown && (
					<ElementLayout.MoveDownMenuItem
						onClick={onMoveDown}
					/>
				)}
				<ElementLayout.DuplicateMenuItem
					onClick={onDuplicate}
				/>
				<ElementLayout.DeleteMenuItem
					onClick={onDelete}
				/>
			</ElementLayout.ActionsMenu>
			<div
				{...setTrackingId(attachable.reducer.path, "__horizontal-line")}
				className={styles.root}
				onClick={() => activate()}
			>
				<hr className={styles.line}/>
			</div>
		</ElementLayout>
	);
};

HorizontalLine.displayName="HorizontalLine";